/* eslint-disable react-refresh/only-export-components */
import * as React from 'react';

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  tabsListVariants,
} from './components/ui/tabs.js';
import { cn } from './lib/utils.js';

export { tabsListVariants };

export function DimeTabs({ ...props }: Readonly<React.ComponentProps<typeof Tabs>>) {
  return <Tabs {...props} />;
}

export function DimeTabsList({
  className,
  variant,
  ...props
}: Readonly<React.ComponentProps<typeof TabsList>>) {
  return (
    <TabsList
      variant={variant}
      // At least full width, and as wide as the tabs once they overflow and scroll, so both rules span every tab.
      className={cn(variant === 'line' && 'w-max min-w-full justify-start border-y', className)}
      {...props}
    />
  );
}

export function DimeTabsTrigger({
  className,
  ...props
}: Readonly<React.ComponentProps<typeof TabsTrigger>>) {
  return (
    <TabsTrigger
      // Overrides the vendor trigger's `flex-1`; the margin keeps a corner count off the next pill.
      className={cn(
        'group-data-[variant=line]/tabs-list:flex-none has-data-[placement=corner]:mr-3',
        className,
      )}
      {...props}
    />
  );
}

export function DimeTabsContent({ ...props }: Readonly<React.ComponentProps<typeof TabsContent>>) {
  return <TabsContent {...props} />;
}

// Small text needs 4.5:1: dark-mode destructive is light, so it takes the page background as its text colour.
const COUNT_TONE = {
  muted: { inline: 'bg-muted text-foreground', corner: 'bg-foreground text-background' },
  destructive: {
    inline: 'bg-destructive text-white dark:text-background',
    corner: 'bg-destructive text-white dark:text-background',
  },
} as const;

const COUNT_PLACEMENT = {
  inline: 'rounded px-1.5 py-0.5 text-xs',
  corner:
    'absolute -top-2 -right-3 z-10 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[0.65rem] leading-none font-semibold ring-2 ring-muted',
} as const;

export function DimeTabCount({
  className,
  children,
  tone = 'muted',
  placement = 'inline',
  ...props
}: Readonly<
  React.ComponentProps<'span'> & {
    tone?: 'muted' | 'destructive';
    placement?: 'inline' | 'corner';
  }
>) {
  return (
    <span
      data-placement={placement}
      className={cn(COUNT_PLACEMENT[placement], COUNT_TONE[tone][placement], className)}
      {...props}
    >
      {/* Without this the trigger's accessible name runs the label into the number -- "Locations5". */}
      <span className="sr-only">, count: </span>
      {children}
    </span>
  );
}
